// src/RootComponent.js
import React, { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useParams,
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from "react-router-dom";
import { LoadingOverlay, Box, Text, Center, Stack, ThemeIcon, ActionIcon, MantineProvider } from "@mantine/core";
import { IconShieldX, IconX } from "@tabler/icons-react";
import App from "./App";
import { DataProvider } from "./context/DataContext";
import NotFound from "./pages/not-found";
import SubRoutes from "./routes";
import {
  FingerprintProvider,
  Fingerprint,
} from "@fingerprint/react";
import * as Sentry from "@sentry/react"; // Import Sentry
import { ensureTrailingSlash } from "./utils/url";
import { getEdgeStartSessionUrl, getEdgeGetSessionUrl, getEdgeRetrieveUrl } from "./utils/edge";
import {
  browserName,
  browserVersion,
  fullBrowserVersion,
  osName,
  osVersion,
  deviceType,
  mobileVendor,
  mobileModel,
  getUA
} from "react-device-detect";

// Sentry is currently disabled
// To re-enable, uncomment the Sentry initialization below and the SentryRoutes variable
/*
Sentry.init({
  dsn: "https://e5bbbf0db439485e92518f5484e1baae@o4504792099258368.ingest.us.sentry.io/4504792101158912",
  integrations: [
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    Sentry.breadcrumbsIntegration({
      console: true,
      dom: true,
      fetch: true,
      history: true,
      xhr: true,
    }),
    Sentry.replayIntegration(),
    Sentry.captureConsoleIntegration(),
  ],
  tracesSampleRate: 1.0, // Adjust based on your need
  tracePropagationTargets: [
    /^https:\/\/fe\.dcams\.app/, // Match fe.dcams.app
    /^https:\/\/vx-fe\.dcams\.app/, // Match vx-fe.dcams.app
  ],

  // Capture Replay for 10% of all sessions,
  // plus for 100% of sessions with an error
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
});
*/

// const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

// Function to get request_id from session_id
async function getRequestIdFromSession(session_id) {
  const response = await fetch(
    process.env.REACT_APP_BACKEND_URL + '/v3/stable/api/frontend/exchangeSessionForToken',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ session_id }) // Payload with the session_id
    }
  )

  if (!response.ok) {
    throw new Error(`Error exchanging session: ${response.statusText}`)
  }

  // Sample response: { "request_id": "1f9dfa59-54fa-4067-9a29-26265342b2f2" }
  const result = await response.json()
  return result.request_id
}

// FingerprintJS config helper — used both in imperative early fingerprint and FingerprintProvider
const getFpjsConfig = () => {
  const hostname = window.location.hostname;
  const isPreviewEnv = hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "[::1]" ||
    hostname === "10.0.2.2" ||
    hostname.includes("preview") ||
    hostname.endsWith("fe-dcams.pages.dev");
  const apiKey = isPreviewEnv ? "jnsYBLlwOmn9MDJb0la2" : "U1OoU5FUoWuUW1gJvyL3";
  return {
    apiKey,
    endpoints: "https://dcams.app/uTU6pIMQFwWuQOfC/AWn7OalBYI7aYzip",
  };
};

const AppWithData = () => {
  const { request_id } = useParams();
  const location = useLocation();
  const [sessionData, setSessionData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [edgeFingerprintActive, setEdgeFingerprintActive] = useState(null);
  const [edgeFingerprintDone, setEdgeFingerprintDone] = useState(false);
  
  // Check fingerprint param directly in the render function to avoid timing issues
  const urlParams = new URLSearchParams(location.search);
  const fingerprintParam = urlParams.get('fingerprint');
  const shouldDisableFingerprint = fingerprintParam === 'f';
  
  // Sentry tag setting is currently disabled
  /*
  useEffect(() => {
    if (request_id) {
      // Set request_id as a tag in Sentry
      Sentry.setTag("request_id", request_id);
    }
  }, [request_id]);
  */

  // Check for frontend query param
  const frontendParam = urlParams.get('frontend');
  const edge = urlParams.get('edge');
  const stateParam = urlParams.get('state');

  // Determine if we should use VX backend based on hostname or query param
  const useVxBackend = window.location.hostname === "vx-fe.dcams.app" || frontendParam === "vx-fe";

  // Check if edge parameter is true OR if request_id/state carries the "-edge" suffix
  const isEdgeFromRequestId = request_id?.endsWith('-edge') || stateParam?.endsWith('-edge');
  const useEdgeEndpoint = edge === "true" || isEdgeFromRequestId;
  
  const vxSessionUrl = ensureTrailingSlash(
    "https://vx.dcams.app/frontend/session/get_session"
  );

  const getPrimaryUrl = useEdgeEndpoint
    ? null // Edge mode uses start_session + get_session flow
    : useVxBackend
    ? vxSessionUrl
    : `${process.env.REACT_APP_BACKEND_URL}/v3/stable/api/frontend/getUrl`;

  const getSecondaryUrl = useEdgeEndpoint
    ? null
    : !useVxBackend
    ? vxSessionUrl
    : `${process.env.REACT_APP_BACKEND_URL}/v3/stable/api/frontend/getUrl`;

  // Edge mode: POST to start_session with device info (one-time)
  const edgeStartSession = async (requestId) => {
    try {
      const payload = {
        request_id: requestId,
        device_info: {
          browserName,
          browserVersion,
          fullBrowserVersion,
          osName,
          osVersion,
          deviceType,
          mobileVendor,
          mobileModel,
          userAgent: getUA
        },
      };

      // Include current URL parameters
      const currentUrlParams = new URLSearchParams(window.location.search);
      const urlParamsObject = {};
      for (const [key, value] of currentUrlParams.entries()) {
        urlParamsObject[key] = value;
      }
      if (Object.keys(urlParamsObject).length > 0) {
        payload.url_params = urlParamsObject;
      }

      const response = await fetch(getEdgeStartSessionUrl(), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        console.warn(`start_session failed: ${response.status} ${response.statusText}`);
        return null;
      }

      const result = await response.json();
      console.log("start_session response:", result);
      return {
        requestId: result.request_id || requestId,
        is_fingerprint_active: result.is_fingerprint_active,
      };
    } catch (error) {
      console.warn("start_session error:", error);
      return null;
    }
  };

  // Edge mode: GET get_session/{request_id} — polls until ready: true
  const edgeGetSession = async (requestId, visitorId) => {
    try {
      let url = `${getEdgeGetSessionUrl()}/${requestId}`;
      if (visitorId) {
        url += `?visitor_id=${encodeURIComponent(visitorId)}`;
      }
      const response = await fetch(url, {
        method: "GET",
      });

      if (!response.ok) {
        console.warn(`get_session failed: ${response.status}`);
        return { notReady: true };
      }

      const result = await response.json();

      // Check if blocked — must be checked first
      if (result.blocked === true) {
        return { blocked: true };
      }

      // Session not ready yet
      if (result.ready === false) {
        return { notReady: true };
      }

      // Check for already_done
      if (result.already_done === true) {
        return result;
      }

      // Session is ready — must have data
      if (!result.data && result.data !== false && result.already_done !== true) {
        return { notReady: true };
      }

      return result;
    } catch (error) {
      console.warn("get_session error:", error);
      return { notReady: true };
    }
  };

  // Non-edge: POST with request_id to get session data
  const fetchAndCheck = async (url, request_id) => {
    try {
      const payload = { request_id };

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (response.status === 404) {
        return { notFound: true };
      }

      if (!response.ok) {
        console.warn(`Error with request to ${url}: ${response.statusText}`);
        return null;
      }

      const result = await response.json();

      // Allow already_done responses to pass through even without data
      if ((!result.data || result.data === false) && result.already_done !== true) {
        return null;
      }

      return result;
    } catch (error) {
      console.warn(`Failed to fetch from ${url}:`, error);
      return null;
    }
  };

  // Fetch the session data first
  useEffect(() => {
    const fetchData = async () => {
      if (!request_id || request_id === 'upload') {
        setIsLoading(false);
        return;
      }

      let localRequestId = request_id;

      // Handle special cases for request_id
      if (localRequestId === 'clear' || localRequestId === 'clear/return' ||
          localRequestId === 'oneid' || localRequestId === 'oneid/return' ||
          localRequestId === 'digilocker' || localRequestId === 'digilocker/return/success' ||
          localRequestId === 'digilocker/return/failure') {
        localRequestId = urlParams.get('state');
        if (localRequestId?.endsWith('-edge')) {
          localRequestId = localRequestId.replace('-edge', '');
        }
      } else if (localRequestId === 'mdl') {
        const session_id = urlParams.get('sessionId');
        if (!session_id) {
          setError('No session_id in query params.');
          setIsLoading(false);
          return;
        }
        try {
          localRequestId = await getRequestIdFromSession(session_id);
        } catch (err) {
          setError('Could not exchange session for token');
          setIsLoading(false);
          return;
        }
      } else if (localRequestId === 'trinsic' || localRequestId === 'bankid-cz' ||
                 localRequestId === 'finnish-trust-network' || localRequestId === 'mojeid' ||
                 localRequestId === 'brazil-cpf') {
        // Trinsic providers use sessionId to exchange for real request_id
        const session_id = urlParams.get('sessionId');
        if (!session_id) {
          setError('No sessionId in query params for Trinsic return.');
          setIsLoading(false);
          return;
        }
        try {
          localRequestId = await getRequestIdFromSession(session_id);
          // If exchange returns null, call idmax retrieve with sessionId (edge mode)
          if (!localRequestId) {
            console.log('Exchange returned null, calling idmax retrieve with sessionId');
            const resultsAccessKey = urlParams.get('resultsAccessKey');
            const retrieveResponse = await fetch(getEdgeRetrieveUrl(), {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ sessionId: session_id, trinsic: true, code: resultsAccessKey }),
            });
            if (retrieveResponse.ok) {
              const retrieveResult = await retrieveResponse.json();
              console.log('IDMax retrieve response:', retrieveResult);
              // Let the window close itself after retrieve completes
              window.close();
              return;
            } else {
              console.error('IDMax retrieve failed:', retrieveResponse.status);
              setError('Could not complete verification');
              setIsLoading(false);
              return;
            }
          }
        } catch (err) {
          setError('Could not exchange session for token (Trinsic)');
          setIsLoading(false);
          return;
        }
      } else if (localRequestId?.endsWith('-edge')) {
        localRequestId = localRequestId.replace('-edge', '');
      }

      let result = null;

      if (useEdgeEndpoint) {
        // Edge mode: 1) POST start_session, 2) poll GET get_session
        const startResult = await edgeStartSession(localRequestId);
        if (!startResult) {
          console.error("Failed to start edge session");
          setError("Failed to load session data");
          setIsLoading(false);
          return;
        }
        const sessionRequestId = startResult.requestId;
        if (startResult.is_fingerprint_active !== undefined) {
          setEdgeFingerprintActive(startResult.is_fingerprint_active);
        }

        // Run fingerprint immediately after start_session, BEFORE polling get_session
        // This ensures the server has fingerprint data before it decides to block
        let edgeVisitorId = null;
        if (startResult.is_fingerprint_active === true && !shouldDisableFingerprint) {
          try {
            console.log("🔑 Running early fingerprint before get_session polling...");
            const fpConfig = getFpjsConfig();
            const fp = Fingerprint.start({
              apiKey: fpConfig.apiKey,
              endpoints: fpConfig.endpoints,
            });
            const fpResult = await fp.get({
              tag: { request_id: sessionRequestId },
            });
            edgeVisitorId = fpResult.visitor_id || null;
            console.log("🔑 Early fingerprint completed, visitor_id:", edgeVisitorId);
            setEdgeFingerprintDone(true);
          } catch (fpError) {
            console.warn("🔑 Early fingerprint failed (non-blocking):", fpError);
          }
        }

        const maxRetries = 30;
        const retryDelay = 2000;
        let retryCount = 0;

        console.log("🔑 Starting get_session polling with visitor_id:", edgeVisitorId);

        while (retryCount < maxRetries) {
          result = await edgeGetSession(sessionRequestId, edgeVisitorId);

          // Session ready or already_done — break out
          if (result && !result.notReady) {
            break;
          }

          retryCount++;
          if (retryCount < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, retryDelay));
          }
        }

        if (!result || result.notReady) {
          console.error("Edge session not ready after polling. result:", result);
          setError("Failed to load session data");
          setIsLoading(false);
          return;
        }

        // Check if blocked by server
        if (result?.blocked) {
          setError("BLOCKED");
          setIsLoading(false);
          return;
        }
      } else {
        // Non-edge mode: POST to primary/secondary URLs
        const maxRetries = 3;
        let retryCount = 0;

        while (retryCount < maxRetries) {
          result = await fetchAndCheck(getPrimaryUrl, localRequestId);

          if (result && !result.notFound) {
            break;
          }

          // If primary failed, try secondary
          console.warn(`Primary URL failed (attempt ${retryCount + 1}/${maxRetries}), trying secondary URL: ${getSecondaryUrl}`);
          result = await fetchAndCheck(getSecondaryUrl, localRequestId);

          if (result && !result.notFound) {
            break;
          }

          retryCount++;
        }

        if (!result || result.notFound) {
          console.error("Failed to fetch data from both primary and secondary URLs after retries. result:", result);
          setError("Failed to load session data");
          setIsLoading(false);
          return;
        }
      }


      // Check if session is already done
      if (result.already_done === true) {
        setError("ALREADY_DONE");
        setIsLoading(false);
        return;
      }

      setSessionData(result);
      setIsLoading(false);
    };

    fetchData();
  }, [request_id]);

  // Send postMessage for ALREADY_DONE - must be before any conditional returns
  React.useEffect(() => {
    if (error === "ALREADY_DONE") {

      // Send payload version
      const payload = {
        messageType: "payload",
        value: {
          event: "ERROR",
          app: null,
          error: "ALREADY DONE"
        }
      };
      window.parent.postMessage(payload, "*");

      // Send simple string version
      window.parent.postMessage("ALREADY DONE", "*");
    }
  }, [error]);

  if (isLoading) {
    return (
      <Box sx={{ position: 'relative', height: '100vh' }}>
        <LoadingOverlay visible={true} overlayBlur={2} />
      </Box>
    );
  }

  if (error) {
    if (error === "BLOCKED") {
      const isIframe = window.self !== window.top;
      return (
        <Box style={{ position: 'relative', height: '100vh' }}>
          {isIframe && (
            <Box
              style={{
                position: 'fixed',
                top: 0,
                right: 0,
                padding: '8px 12px',
                zIndex: 1000,
              }}
            >
              <ActionIcon
                variant="light"
                onClick={() => window.parent.postMessage("CLOSE", "*")}
              >
                <IconX stroke={1.5} size={18} />
              </ActionIcon>
            </Box>
          )}
          <Center style={{ height: '100%', flexDirection: 'column', padding: '20px' }}>
            <Stack align="center" spacing="md">
              <ThemeIcon variant="light" color="red" size={80} radius="xl">
                <IconShieldX size={50} stroke={1.5} />
              </ThemeIcon>
              <Text size="xl" weight={700} align="center">
                Unable to Continue
              </Text>
            </Stack>
          </Center>
        </Box>
      );
    }

    const errorTitle = error === "ALREADY_DONE" ? "Link Expired" : "Error";
    const errorMessage = error === "ALREADY_DONE"
      ? "This link is either invalid, expired, or has already been used"
      : error;

    return (
      <Center style={{ height: '100vh', flexDirection: 'column', padding: '20px' }}>
        <Text color="red" size="xl" mb={10}>❌</Text>
        <Text size="xl" weight={700} mb={10}>{errorTitle}</Text>
        <Text align="center">{errorMessage}</Text>
      </Center>
    );
  }

  // Determine if fingerprinting should be disabled based on URL param, settings, or edge start_session
  // If early fingerprint was already done (edge mode), skip FpjsProvider entirely
  const isFingerPrintActive = edgeFingerprintActive !== null
    ? edgeFingerprintActive !== false
    : sessionData?.data?.settings?.is_fingerprint_active !== false;
  const fingerprintDisabled = shouldDisableFingerprint || !isFingerPrintActive || edgeFingerprintDone;

  // Check for test mode from session data (edge mode)
  const isTestMode = sessionData?.test_mode === true;

  // Test mode banner component
  const TestModeBanner = () => (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        backgroundColor: '#ff9800',
        color: '#000',
        textAlign: 'center',
        padding: '2px 8px',
        fontSize: '10px',
        fontWeight: 'bold',
        zIndex: 99999,
      }}
    >
      Test Mode Active
    </div>
  );

  // Skip FpjsProvider when disabled via URL param or settings
  if (fingerprintDisabled) {
    return (
      <>
        {isTestMode && <TestModeBanner />}
        <DataProvider
          request_id={request_id}
          fingerprintDisabled={true}
          initialSessionData={sessionData}
        >
          <App routes={<SubRoutes />} />
        </DataProvider>
      </>
    );
  }

  // Regular flow with fingerprinting (non-edge, or edge without early fingerprint)
  const fpConfig = getFpjsConfig();

  return (
    <>
      {isTestMode && <TestModeBanner />}
      <FingerprintProvider
        apiKey={fpConfig.apiKey}
        endpoints={fpConfig.endpoints}
      >
        <DataProvider
          request_id={request_id}
          fingerprintDisabled={false}
          initialSessionData={sessionData}
        >
          <App routes={<SubRoutes />} />
        </DataProvider>
      </FingerprintProvider>
    </>
  );
};

const RootComponent = () => {
  // Read colorScheme and primaryColor from URL immediately for initial render
  const urlParams = new URLSearchParams(window.location.search);
  const colorScheme = urlParams.get('colorScheme') || 'light';
  const primaryColor = urlParams.get('primaryColor');

  // Build theme object — include primaryColor as a custom shade if provided
  const themeOverrides = { colorScheme };
  if (primaryColor) {
    try {
      const hex = primaryColor.startsWith('#') ? primaryColor : `#${primaryColor}`;
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      // Mix with white (lighter) or black (darker) to create 10 shades
      const mix = (c, target, weight) => Math.round(c + (target - c) * weight);
      const toHex = (r, g, b) =>
        `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
      const weights = [0.85, 0.7, 0.55, 0.4, 0.25, 0.1, 0, 0.15, 0.3, 0.45];
      const shades = weights.map((w, i) =>
        i <= 5
          ? toHex(mix(r, 255, w), mix(g, 255, w), mix(b, 255, w))
          : toHex(mix(r, 0, w), mix(g, 0, w), mix(b, 0, w))
      );
      themeOverrides.colors = { brand: shades };
      themeOverrides.primaryColor = 'brand';
    } catch (e) {
      // Invalid color format — fall back to default theme
    }
  }

  return (
    <MantineProvider
      theme={themeOverrides}
      withGlobalStyles
      withNormalizeCSS
    >
      <Router>
        {/* Sentry error boundary is currently disabled
        <Sentry.ErrorBoundary fallback={<div>An error has occurred.</div>}>
        */}
          <Routes>
            <Route path="/:request_id/*" element={<AppWithData />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        {/* </Sentry.ErrorBoundary> */}
      </Router>
    </MantineProvider>
  );
};

export default RootComponent;
